import React, { useState, useEffect, useRef } from 'react';
import useFetchData from '../../../hooks/useFetchData';
import { useParams, Outlet } from 'react-router-dom';
import Profile from './Informations/Profile';
import { useBreadcrumbs } from '../../../context/BreadcrumbsContext';
import useBreadcrumbMapping from '../../../hooks/useBreadcrumbMapping';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import api from '../../../middleware/api';
import LoadingSpinner from '../../Common/Loading/LoadingSpinner';
import BBCodeEditor from '../../Common/TextEditor/BBCodeEditor';
import { Helmet } from 'react-helmet-async';

const CustomerOverview = () => {
  const { userId } = useParams();
  const { data: customerData, error, isLoading, setData } = useFetchData(`/customers/read/${userId}`);
  const profileRef = useRef(null);
  const [contentWidth, setEditorWidth] = useState('100%');
  const [contentHeight, setContentHeight] = useState(null);
  const [showContent, setShowContent] = useState(() => {
    return localStorage.getItem('customerOverviewDisplay') !== 'hidden';
  });
  const capsules = customerData?.capsules || [];
  const [isProfileMounted, setIsProfileMounted] = useState(false);

  // Utiliser le hook pour enregistrer automatiquement le mapping customer
  useBreadcrumbMapping(
    userId, 
    customerData ? `${customerData.firstname} ${customerData.lastname}` : null,
    'customer',
    !!customerData
  );

  const saveComment = async (content) => {
    try {
      await api.patch(`/customers/update-comment/${customerData.id}`, { informations: content });
      setData(prev => ({ ...prev, informations: content }));
      return true;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du commentaire:', error);
      throw error;
    }
  };


  useEffect(() => {
    const updateDimensions = () => {
      if (profileRef.current) {
        const profileHeight = profileRef.current.offsetHeight;
        const profileWidth = profileRef.current.offsetWidth + 16;

        if (window.innerWidth > 1024) {
          setContentHeight(`${profileHeight}px`);
          setEditorWidth(`calc(100% - ${profileWidth}px)`);
        } else {
          setContentHeight('200px');
          setEditorWidth('100%');
        }
      }
    };

    if (isProfileMounted && showContent) {
      updateDimensions();
    }

    const handleResize = () => {
      if (showContent) {
        updateDimensions();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isProfileMounted, showContent]);

  const toggleContent = () => {
    const newValue = !showContent;
    setShowContent(newValue);
    localStorage.setItem('customerOverviewDisplay', newValue ? 'visible' : 'hidden');
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div>Error loading user data</div>;

  return (
    <>
      <Helmet>
        <title>{customerData.firstname} {customerData.lastname} - Verbatim</title>
      </Helmet>

      <div className='m-auto w-[98%] 2xl:w-11/12 '>
        <button
          onClick={toggleContent}
          className="absolute right-4 cursor-pointer flex items-center top-10 p-1 z-[99] rounded-full bg-white shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200 group"
          aria-label={showContent ? 'Masquer les informations utilisateur' : 'Afficher les informations utilisateur'}
          title={showContent ? 'Masquer les informations utilisateur' : 'Afficher les informations utilisateur'}
        >
          {showContent ? (
            <EyeIcon className="w-5 h-5 text-gray-600 group-hover:text-gray-800" />
          ) : (
            <EyeSlashIcon className="w-5 h-5 text-gray-600 group-hover:text-gray-800" />
          )}
        </button>

        {showContent && (
          <div className='flex gap-4 relative h-full max-h-72'>
            <Profile
              ref={profileRef}
              customer={customerData}
              setCustomerData={setData}
              onMount={() => setIsProfileMounted(true)}
              onUnmount={() => setIsProfileMounted(false)}
            />
          </div>
        )}
        <div className={`ml-auto h-auto w-full ${!showContent ? 'hidden' : ''}`}>
          <BBCodeEditor
            onSave={saveComment}
            initialValue={customerData.informations}
            debounceTime={0}
          />
        </div>

        <Outlet context={{
          customer: customerData,
          setData,
          capsules: capsules
        }} />
      </div>
    </>
  );
};

export default CustomerOverview;
